import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ACTIVE_HIT_CLASS,
  collectHits,
  setActiveHit,
  stepIndex,
} from './renderers/hitNavigation'

export interface HitNavigation {
  /** Hits currently in the preview. */
  total: number
  /** 1-based position of the current hit, 0 when there is none. */
  position: number
  next: () => void
  previous: () => void
}

/**
 * Stepping through the search hits inside a preview (#627).
 *
 * Reads the DOM rather than a count kept alongside it, so both highlighting
 * paths are covered by one implementation and the number shown can never
 * disagree with what is actually marked.
 *
 * A PDF fills in over time — every page mounts, but each renders its text
 * layer asynchronously — so the hit list is re-collected whenever the subtree
 * changes. Only `childList` is observed: marking the active hit is an
 * attribute change, which would otherwise re-trigger the observer forever.
 */
export function useHitNavigation(
  rootRef: React.RefObject<HTMLElement | null>,
  term: string,
): HitNavigation {
  const [total, setTotal] = useState(0)
  const [index, setIndex] = useState(0)
  const hitsRef = useRef<HTMLElement[]>([])
  const frameRef = useRef<number | null>(null)

  // A term the user is still typing invalidates the previous position.
  useEffect(() => {
    setIndex(0)
  }, [term])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const recollect = () => {
      frameRef.current = null
      const hits = collectHits(root)
      hitsRef.current = hits
      setTotal(hits.length)
    }
    const schedule = () => {
      if (frameRef.current !== null) return
      frameRef.current = window.requestAnimationFrame(recollect)
    }

    recollect()
    const observer = new MutationObserver(schedule)
    observer.observe(root, { childList: true, subtree: true })
    return () => {
      observer.disconnect()
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
  }, [rootRef, term])

  // Move the marking and bring the hit into view. Runs on every change of the
  // hit list too, so the first hit of a PDF page that only just rendered still
  // gets focused.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const active = setActiveHit(root, hitsRef.current, index)
    active?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [rootRef, index, total])

  // Nothing marked any more (term cleared, document swapped) — drop the
  // leftover styling instead of leaving one hit permanently highlighted.
  useEffect(() => {
    const root = rootRef.current
    if (!root || total > 0) return
    root.querySelectorAll<HTMLElement>(`.${ACTIVE_HIT_CLASS}`).forEach((el) => {
      el.classList.remove(ACTIVE_HIT_CLASS)
    })
  }, [rootRef, total])

  const next = useCallback(() => setIndex((i) => stepIndex(i, hitsRef.current.length, 1)), [])
  const previous = useCallback(() => setIndex((i) => stepIndex(i, hitsRef.current.length, -1)), [])

  return { total, position: total === 0 ? 0 : index + 1, next, previous }
}
